import React, { useState } from 'react';
import Modal from 'react-modal';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { translate } from 'translations/i18n';
import { Spinner } from '@companycam/slab-web';
import styled from 'styled-components';

import ConfirmRoot from './confirm/ConfirmRoot';
import CoverPhoto from './CoverPhoto';
import Main from './Main';
import SelectPhotos from './SelectPhotos';

import {
  MAIN,
  SELECT_PHOTOS,
  SELECT_COVER_PHOTO,
  type WizardView,
} from '../constants';
import { useModal } from 'components/shared/hooks';
import { useConfirmationModal } from './confirm/useConfirmModal';
import toast from 'helpers/toast';

import {
  type ShowcaseProject,
  type CreateShowcaseProjectParams,
  type ShowcaseProjectMediaItem,
  useCreateShowcaseProject,
  useGetShowcaseProjectMedia,
  useUpdateShowcaseProject,
} from 'javascripts/data/v3/showcases';
import type { ProjectDatabaseId } from 'data/v1';

type FormBodyProps = {
  view: WizardView;
  showConfirm: () => void;
  hideModal: () => void;
  setView: (view: WizardView) => void;
  projectDatabaseId: ProjectDatabaseId;
  isEditing: boolean;
  isSubmitting: boolean;
};

export type ShowcaseWizardRootProps = {
  existingShowcaseProject: ShowcaseProject | null;
  isEditing: boolean;
  projectDatabaseId: ProjectDatabaseId;
  afterUpdateOrDelete?: () => void;
  afterCreate?: () => void;
};

export type ShowcaseWizardFormData = Pick<
  ShowcaseProject,
  'title' | 'description' | 'featuredImage' | 'visible'
> & {
  assets: ShowcaseProjectMediaItem[];
  // using this instead of the materialsUsed and projectTypes to maintain backwards compatibility
  materials: ShowcaseProject['materialsUsed'];
  types: ShowcaseProject['projectTypes'];
};

const LoadingContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
`;

const FormBody = ({
  view,
  showConfirm,
  hideModal,
  setView,
  projectDatabaseId,
  isEditing,
  isSubmitting,
}: FormBodyProps) => {
  switch (view) {
    case SELECT_PHOTOS:
      return (
        <SelectPhotos
          onConfirm={hideModal}
          setView={setView}
          projectDatabaseId={projectDatabaseId}
        />
      );
    case SELECT_COVER_PHOTO:
      return (
        <CoverPhoto
          projectDatabaseId={projectDatabaseId}
          setView={setView}
          onConfirm={hideModal}
        />
      );
    default:
      return (
        <Main
          isEditing={isEditing}
          setView={setView}
          showConfirm={showConfirm}
          onConfirm={hideModal}
          projectDatabaseId={projectDatabaseId}
          isSubmitting={isSubmitting}
        />
      );
  }
};

export default function ShowcaseWizardRoot({
  existingShowcaseProject,
  isEditing,
  projectDatabaseId,
  afterUpdateOrDelete,
  afterCreate,
}: ShowcaseWizardRootProps) {
  const [view, setView] = useState<WizardView>(MAIN);
  const { showConfirm, cancelConfirm, confirm } = useConfirmationModal();
  const { hideModal } = useModal();

  const { data, isLoading } = useGetShowcaseProjectMedia({
    projectDatabaseId,
    params: { shared: true, assetType: 'image' },
  });

  const allSharedAssets =
    data?.pages?.map((page) => page?.data?.items).flat() || [];

  const initialValues: ShowcaseWizardFormData = {
    featuredImage: existingShowcaseProject?.featuredImage,
    description: existingShowcaseProject?.description ?? '',
    assets: allSharedAssets,
    materials: existingShowcaseProject?.materialsUsed ?? [],
    visible: existingShowcaseProject?.visible ?? false,
    title: existingShowcaseProject?.title ?? '',
    types: existingShowcaseProject?.projectTypes ?? [],
  };

  const validationSchema = Yup.object({
    title: Yup.string().required(
      translate('showcases.create.v2PublishWizard.noTitleError'),
    ),
    types: Yup.array()
      .of(Yup.string())
      .min(1, translate('showcases.create.v2PublishWizard.minTypesError')),
    materials: Yup.array()
      .of(Yup.string())
      .min(1, translate('showcases.create.v2PublishWizard.minProductsError')),
    assets: Yup.array().min(
      1,
      translate('showcases.create.v2PublishWizard.minAssetsError'),
    ),
    featuredImage: Yup.object({
      id: Yup.number().required(),
      assetPreviewLarge: Yup.string().required(),
    }).required(
      translate('showcases.create.v2PublishWizard.minCoverPhotoError'),
    ),
  });

  const createShowcaseProject = useCreateShowcaseProject({
    projectDatabaseId,
    onSuccess: () => {
      hideModal();
      afterCreate?.();
      toast.success(
        translate('showcases.create.v2PublishWizard.showcaseCreated'),
      );
    },
    onError: () => {
      toast.error(translate('showcases.create.v2PublishWizard.showcaseFailed'));
    },
  });

  const updateShowcaseProject = useUpdateShowcaseProject({
    projectDatabaseId,
    onSuccess: () => {
      hideModal();
      afterUpdateOrDelete?.();
      toast.success(
        translate('showcases.v2PublishWizard.editShowcase.success'),
      );
    },
    onError: () => {
      toast.error(translate('showcases.v2PublishWizard.editShowcase.error'));
    },
  });

  const isSubmitting =
    createShowcaseProject.isPending || updateShowcaseProject.isPending;

  const onSubmit = async (values: ShowcaseWizardFormData) => {
    const visibleMedia = values.assets.map((asset) => asset.id);

    const showcaseProjectData: CreateShowcaseProjectParams = {
      title: values.title,
      description: values.description,
      materials: values.materials,
      types: values.types,
      featuredImageId: values?.featuredImage?.id,
      visibleMedia,
    };

    if (isEditing) {
      const hiddenMedia = allSharedAssets
        .filter((asset) => !visibleMedia.includes(asset.id))
        .map((asset) => asset.id);

      updateShowcaseProject.mutate({
        ...showcaseProjectData,
        visibleMedia,
        hiddenMedia,
        visible: values.visible,
      });
    } else {
      createShowcaseProject.mutate(showcaseProjectData);
    }
  };

  return (
    <Modal
      isOpen
      onRequestClose={hideModal}
      ariaHideApp={false}
      shouldCloseOnOverlayClick={false}
      overlayClassName="modal"
      className={`modal-content-wrap publishing-wizard ${
        confirm.isVisible ? 'is-confirming' : ''
      }`}
    >
      {isLoading ? (
        <LoadingContainer>
          <Spinner
            accessibilityLabel={translate('common.loading')}
            size="large"
          />
        </LoadingContainer>
      ) : (
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          validateOnMount
          validateOnChange
        >
          {({ handleSubmit }) =>
            confirm.isVisible ? (
              <ConfirmRoot
                confirm={confirm}
                cancelConfirm={cancelConfirm}
                onSuccessfulDelete={afterUpdateOrDelete}
              />
            ) : (
              <form onSubmit={handleSubmit}>
                <FormBody
                  view={view}
                  showConfirm={showConfirm}
                  hideModal={hideModal}
                  setView={setView}
                  projectDatabaseId={projectDatabaseId}
                  isEditing={isEditing}
                  isSubmitting={isSubmitting}
                />
              </form>
            )
          }
        </Formik>
      )}
    </Modal>
  );
}
